import {assert} from 'Asserts';
import type {ColumnGroup} from 'notebook/structured-tables/columns/ColumnDefinitions';
import type {UnitTypePredicates} from 'notebook/structured-tables/columns/UnitTypePredicates';
import {ColumnConstraint} from 'notebook/tables/constants/TableColumnConstants';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import {findColumnIndexByGroup} from 'notebook/tables/search-scoping/rowScopedSearch';

/**
 * Identifies the product-specific logic that derives a unit type restriction from a sibling cell. Each type maps
 * to a hook in ROW_SCOPED_UNIT_TYPE_FILTER_HOOKS (see RowScopedUnitTypeFilters).
 */
export enum RowScopedUnitTypeFilterType {
  STOICHIOMETRY_LOT_CONCENTRATION = 'STOICHIOMETRY_LOT_CONCENTRATION',
}

/**
 * Generic support for narrowing a UNIT_REFERENCE column's picker based on the value of a *sibling* cell in the
 * same row. Kept as plain data so it can live on a column definition, mirroring RowScopedSearchConfig.
 */
export interface RowScopedUnitTypeFilterConfig {
  /** Column group of the sibling cell whose evaluated value drives the restriction. */
  sourceColumnGroup: NonNullable<ColumnGroup>;
  type: RowScopedUnitTypeFilterType;
}

/**
 * `unitTypeId` and a row-scoped unit type filter are two ways to narrow the same picker, so a column may set only
 * one of them.
 */
export function assertSingleUnitScoping({
  unitTypeId,
  rowScopedUnitTypeFilter,
}: {
  unitTypeId: string | null | undefined;
  rowScopedUnitTypeFilter: RowScopedUnitTypeFilterConfig;
}): void {
  assert(
    unitTypeId == null,
    'A unit reference column cannot set both unitTypeId and rowScopedUnitTypeFilter.',
    {unitTypeId, rowScopedUnitTypeFilter}
  );
}

export type RowScopedUnitTypeFilterResult =
  /** The restriction isn't known yet; the picker must not offer units until it is. */
  | {loading: true}
  /** `predicateType` replaces the column's own predicate; null keeps the column's predicate. */
  | {loading: false; predicateType: UnitTypePredicates | null};

export interface RowScopedUnitTypeFilterArgs {
  /** Evaluated sibling cell named by RowScopedUnitTypeFilterConfig.sourceColumnGroup, or null if the row has none. */
  sourceCell: EvaluatedCell | null;
}

/** Evaluated cell of the given column group in the row, or null if the row has no such cell. */
export function findEvaluatedCellByGroup(
  columnGroup: NonNullable<ColumnGroup>,
  params: {
    columnConstraints: Array<ColumnConstraint | null>;
    evaluatedRow: Array<EvaluatedCell>;
  }
): EvaluatedCell | null {
  const columnIndex = findColumnIndexByGroup(params.columnConstraints, columnGroup);
  if (columnIndex === -1) {
    return null;
  }
  return params.evaluatedRow[columnIndex] ?? null;
}
